/**
 * Live-send registry (session-hardening S1, Miles 2026-07-12).
 *
 * The pre-flight stamp mints a clientTaskId and stamps it onto the visible
 * pending widget BEFORE the network call — by design (refresh-recovery). But
 * the resume effect keys off `state.messages`, so the stamp itself wakes it,
 * and its `isStreaming` guard only turns true AFTER `sendTurn` finishes the
 * session pre-flight (a seconds-wide network window in a fresh thread). This
 * module makes the guard structural: a task id registered here is being
 * driven by a live send in THIS tab right now, and no resume/re-attach path
 * may claim it, regardless of React state timing.
 *
 * Module singleton (not React state) on purpose: registration must be
 * synchronous with the mint, visible to every hook instance, and immune to
 * render batching. Entries carry a registration timestamp so an entry
 * orphaned by a crashed send path self-expires instead of muting resume for
 * that task forever.
 */

const LIVE_SEND_TTL_MS = 10 * 60 * 1000; // generously above any send round-trip

const liveSends = new Map<string, number>();

function prune(nowMs: number): void {
  for (const [taskId, registeredAt] of liveSends) {
    if (nowMs - registeredAt > LIVE_SEND_TTL_MS) liveSends.delete(taskId);
  }
}

/** Register a freshly minted clientTaskId as live-send-in-flight. */
export function registerLiveSend(taskId: string | null | undefined): void {
  const id = (taskId || '').trim();
  if (!id) return;
  const now = Date.now();
  prune(now);
  liveSends.set(id, now);
}

/** Clear a task id once its sendTurn settles (success, failure, or throw). */
export function clearLiveSend(taskId: string | null | undefined): void {
  const id = (taskId || '').trim();
  if (!id) return;
  liveSends.delete(id);
}

/** True while a live send in this tab is driving the task id. */
export function isLiveSendInFlight(taskId: string | null | undefined): boolean {
  const id = (taskId || '').trim();
  if (!id) return false;
  const registeredAt = liveSends.get(id);
  if (registeredAt === undefined) return false;
  if (Date.now() - registeredAt > LIVE_SEND_TTL_MS) {
    liveSends.delete(id);
    return false;
  }
  return true;
}

export function __resetLiveSendRegistryForTests(): void {
  liveSends.clear();
}
